import React, { useState, useCallback, useRef } from 'react';
import { BorderRadiusConfig, AnimationConfig, BarClickData } from '../../types';
import { roundedRectPath } from '../../utils/svg';

interface BarProps {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  borderRadius?: number | BorderRadiusConfig;
  opacity?: number;
  animation?: AnimationConfig;
  animationDelay?: number;
  datasetIndex: number;
  dataIndex: number;
  value: number | [number, number];
  label: string;
  datasetLabel: string;
  onClick?: (event: React.MouseEvent, data: BarClickData) => void;
  onHover?: (event: React.MouseEvent, data: BarClickData | null, mousePos?: { x: number; y: number }) => void;
  hoverFill?: string;
  hoverStroke?: string;
  className?: string;
  isMobile?: boolean;
}

/**
 * Bar component - A single bar in the chart
 * Accessible with ARIA attributes and keyboard support
 */
export const Bar: React.FC<BarProps> = ({
  x,
  y,
  width,
  height,
  fill,
  stroke,
  strokeWidth = 0,
  borderRadius = 0,
  opacity = 1,
  animation,
  animationDelay = 0,
  datasetIndex,
  dataIndex,
  value,
  label,
  datasetLabel,
  onClick,
  onHover,
  hoverFill,
  hoverStroke,
  className,
  isMobile = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const barRef = useRef<SVGPathElement>(null);

  // Handle zero or negative dimensions
  const absWidth = Math.abs(width);
  const absHeight = Math.abs(height);
  const adjustedX = width < 0 ? x + width : x;
  const adjustedY = height < 0 ? y + height : y;

  // Don't render if bar is too small
  if (absWidth < 0.5 || absHeight < 0.5) return null;

  const clickData: BarClickData = {
    datasetIndex,
    dataIndex,
    value,
    label,
    datasetLabel,
  };

  // Format value for accessibility
  const formattedValue = Array.isArray(value) 
    ? `${value[0]} to ${value[1]}` 
    : value.toLocaleString();

  const handleClick = useCallback((e: React.MouseEvent) => {
    onClick?.(e, clickData);
  }, [onClick, clickData]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent, clickData);
    }
  }, [onClick, clickData]);

  const getMousePosition = useCallback((e: React.MouseEvent): { x: number; y: number } => {
    const svg = barRef.current?.ownerSVGElement;
    if (!svg) return { x: e.clientX, y: e.clientY };
    
    const svgRect = svg.getBoundingClientRect();
    return {
      x: e.clientX - svgRect.left,
      y: e.clientY - svgRect.top,
    };
  }, []);

  const handleMouseEnter = useCallback((e: React.MouseEvent) => {
    setIsHovered(true);
    const mousePos = getMousePosition(e);
    onHover?.(e, clickData, mousePos);
  }, [onHover, clickData, getMousePosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isHovered) {
      const mousePos = getMousePosition(e);
      onHover?.(e, clickData, mousePos);
    }
  }, [isHovered, onHover, clickData, getMousePosition]);

  const handleMouseLeave = useCallback((e: React.MouseEvent) => {
    setIsHovered(false);
    onHover?.(e, null);
  }, [onHover]);

  // Touch events for mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isMobile) {
      setIsHovered(true);
      const touch = e.touches[0];
      const svg = barRef.current?.ownerSVGElement;
      if (svg) {
        const svgRect = svg.getBoundingClientRect();
        const mousePos = {
          x: touch.clientX - svgRect.left,
          y: touch.clientY - svgRect.top,
        };
        onHover?.(e as unknown as React.MouseEvent, clickData, mousePos);
      }
    }
  }, [isMobile, onHover, clickData]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (isMobile) {
      // Delay hiding tooltip slightly for better UX
      setTimeout(() => {
        setIsHovered(false);
        onHover?.(e as unknown as React.MouseEvent, null);
      }, 1500);
    }
  }, [isMobile, onHover]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setIsHovered(true);
    // Trigger hover callback with bar center position
    const centerX = adjustedX + absWidth / 2;
    const centerY = adjustedY;
    onHover?.({} as React.MouseEvent, clickData, { x: centerX, y: centerY });
  }, [onHover, clickData, adjustedX, adjustedY, absWidth]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    setIsHovered(false);
    onHover?.({} as React.MouseEvent, null);
  }, [onHover]);

  const isActive = isHovered || isFocused;
  const currentFill = isActive && hoverFill ? hoverFill : fill;
  const currentStroke = isActive && hoverStroke ? hoverStroke : stroke;

  // Generate path for rounded rectangle
  const path = roundedRectPath(adjustedX, adjustedY, absWidth, absHeight, borderRadius);

  // Animation styles
  const animationStyle: React.CSSProperties = animation?.enabled ? {
    transition: `all ${animation.duration}ms ${animation.easing}`,
    transitionDelay: `${animationDelay}ms`,
  } : {};

  // Focus ring style
  const focusStyle: React.CSSProperties = isFocused ? {
    filter: 'drop-shadow(0 0 3px rgba(0, 100, 255, 0.8))',
  } : {};

  return (
    <path
      ref={barRef}
      d={path}
      fill={currentFill}
      stroke={currentStroke || (isFocused ? '#0066ff' : undefined)}
      strokeWidth={isFocused ? Math.max(2, strokeWidth) : strokeWidth}
      opacity={opacity}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onFocus={handleFocus}
      onBlur={handleBlur}
      tabIndex={0}
      role="graphics-symbol"
      aria-roledescription="bar"
      aria-label={`${datasetLabel}: ${label}, value ${formattedValue}`}
      aria-describedby={undefined}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        outline: 'none',
        ...animationStyle,
        ...focusStyle,
      }}
      className={className}
    />
  );
};

export default Bar;
